import { Map, fromJS } from "immutable";
import Cookie from "js-cookie";
import { isMobileDevice } from "@/utils/browser";
import { baseTheme } from "@/theme/Theme-variable";
import {
  SHOW_SIDE_NAV,
  HIDE_SIDE_NAV,
  OPEN_PROJECT_SELECT,
  CLOSE_PROJECT_SELECT,
  SET_FOCUSED_PROJECT,
  RESET_FOCUSED_PROJECT,
  OPEN_PROJECT_DROPDOWN,
  CLOSE_PROJECT_DROPDOWN,
  SET_FOCUSED_HUB,
  RESET_FOCUSED_HUB,
  OPEN_PROJECT_TEAMMATE_INVITE,
} from "./sideNavActions";

/**
 * Below `md` the sidebar is a temporary Drawer — a modal over the page, not a
 * gutter beside it (Sidebar.tsx). Opening it unasked would cover the content,
 * so the pre-paint default has to answer the same question MainLayout's
 * `breakpoints.up("md")` does, off the same value rather than a copy of it.
 * `isMobileDevice` cannot stand in for this: it reads the user agent, so a
 * desktop browser at a narrow width passes it.
 */
const isDesktopViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(`(min-width:${baseTheme.breakpoints.values.md}px)`).matches;

/**
 * Whether the sidebar should already be open on the very first render.
 *
 * `SHOW_SIDE_NAV` is dispatched from `loadAllData`, which `initializeApp` only
 * reaches after `fetchUser` and then `getAuthUser` have both resolved. Waiting
 * for two sequential round trips before opening the sidebar means MainLayout's
 * page gutter goes 0px -> 264px after first paint, sliding the whole page
 * sideways on every route — 0.127 of the measured CLS. Deciding it here from
 * state we already have locally keeps the gutter correct from the first frame.
 *
 * Same conditions `loadAllData` and the layout apply later, plus the token
 * check the gutter rule implies: it only reserves space a signed-in sidebar
 * will fill.
 */
const defaultMenuOpen = () =>
  Boolean(Cookie.get("token")) && !isMobileDevice() && isDesktopViewport();

// Read fresh on every fallback rather than captured once at module load: the
// root reducer drops sideNav's slice on RESET_TO_PUBLIC_DATA (logout), and by
// then the token cookie is gone and the sidebar must come back closed.
const initSideNav = () =>
  Map(
    fromJS({
      menuOpen: defaultMenuOpen(),
      focusedProject: null,
      focusedHub: null,
      projectSelectOpen: false,
      projectDropDownOpen: false,
      projectInviteTeammate: null,
    })
  );

function sideNav(state = initSideNav(), action) {
  switch (action.type) {
    case SHOW_SIDE_NAV:
      return state.set("menuOpen", true);
    case HIDE_SIDE_NAV:
      return state.set("menuOpen", false);
    case OPEN_PROJECT_SELECT:
      return state.set("projectSelectOpen", true);
    case CLOSE_PROJECT_SELECT:
      return state.set("projectSelectOpen", false);
    case SET_FOCUSED_PROJECT:
      return state
        .set("focusedProject", action.projectId)
        .set("projectSelectOpen", false)
        .set("projectDropDownOpen", false);
    case RESET_FOCUSED_PROJECT:
      return state
        .set("focusedProject", null)
        .set("projectDropDownOpen", false);
    case OPEN_PROJECT_DROPDOWN:
      return state.set("projectDropDownOpen", true);
    case CLOSE_PROJECT_DROPDOWN:
      return state.set("projectDropDownOpen", false);
    case SET_FOCUSED_HUB:
      return state.set("focusedHub", action.hubId);
    case RESET_FOCUSED_HUB:
      return state.set("focusedHub", null);
    case OPEN_PROJECT_TEAMMATE_INVITE:
      return state.set("projectInviteTeammate", action.projectId);
    default:
      return state;
  }
}

export default sideNav;
